/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useRef } from 'react';
import isNil from 'lodash-es/isNil';

import { SelectOption } from 'interfaces/global';
import { StyleSheet, css } from 'aphrodite';
import Select, { components, OptionProps, MultiValueProps } from 'react-select';
import {
  borderColor,
  primaryColor,
  secondaryColor,
  white
} from '../styles/GlobalStyles';
import FlexSection from '../FlexSection';
import { CUSTOM_VALUES } from 'enums/analytics';
import { trackEvent } from 'utils/segmentTracker';

export type Value = string | string[] | null;

interface Props {
  dataTestId: string;
  defaultValue?: Value;
  onChange: (value: Value) => void;
  options: SelectOption[];
  isClearable?: boolean;
  name?: string;
  isMulti?: boolean;
  inputId?: string;
  styles?: Record<string, any>;
  isLoading?: boolean;
  fetchOptions?: () => void;
  placeholder?: string;
  label?: string;
  dataTrackSelect?: string;
}

const Styles = StyleSheet.create({
  transparent: {
    background: 'transparent'
  },
  checkBox: {
    color: primaryColor
  },
  checkboxLabel: {
    background: 'transparent',
    padding: 0,
    border: 0
  },
  selectAllOption: {
    borderBottom: `1px solid ${borderColor}`,
    marginBottom: '10px'
  }
});

const commonStyles = {
  control: (styles: any) => ({ ...styles, backgroundColor: white }),
  option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {
    return {
      ...styles,
      fontWeight: isSelected ? 'bold' : '',
      backgroundColor: isDisabled
        ? null
        : isSelected
        ? white
        : isFocused
        ? secondaryColor
        : null,
      color: isDisabled ? '#ccc' : isSelected ? primaryColor : data.color,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor: !isDisabled && (isSelected ? data.color : white)
      }
    };
  }
};

const multiStyles = {
  ...commonStyles,
  multiValue: (styles: any) => {
    return {
      ...styles,
      color: primaryColor,
      backgroundColor: primaryColor,
      borderRadius: '4px'
    };
  },
  multiValueLabel: (styles: any) => ({
    ...styles,
    color: 'white'
  }),
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: 'white',
    ':hover': {
      backgroundColor: secondaryColor,
      color: primaryColor
    }
  })
};

const singleStyles = {
  ...commonStyles,
  input: (styles: any) => ({ ...styles }),
  placeholder: (styles: any) => ({ ...styles }),
  singleValue: (styles: any) => ({ ...styles }),
  control: (styles: any) => ({
    ...styles,
    border: '2px solid #8082a9',
    borderRadius: '8px',
    padding: '4px'
  })
};

const Checkbox = ({ checked }: { checked: boolean }) => {
  return (
    <>
      <div className={`ds-checkbox ${css(Styles.transparent)}`}>
        <input
          type="checkbox"
          checked={checked}
          className="checkbox"
          name="checkbox"
          value="checkbox"
          onChange={() => null}
        />
        <label
          id="test-label"
          htmlFor="checkbox"
          className={`ds-btn ${css(Styles.checkboxLabel)}`}
        >
          <i className={`materialIcon --micro _off ${css(Styles.checkBox)}`}>
            check_box_outline_blank
          </i>
          <i
            className={`materialIcon --micro _on ${css(Styles.checkBox)}`}
            style={{ color: primaryColor }}
          >
            check_box
          </i>
        </label>
      </div>
    </>
  );
};

const Option = (props: OptionProps<any>) => {
  return (
    <div
      className={
        props.label === selectAllOption.label ? css(Styles.selectAllOption) : ''
      }
    >
      <components.Option {...props}>
        <FlexSection>
          <Checkbox checked={props.isSelected} />
          <label
            data-testid={props.label}
            className={`_marginTop--xxxsmall ${
              props.isSelected ? 'selected' : ''
            }`}
          >
            {props.label}
          </label>
        </FlexSection>
      </components.Option>
    </div>
  );
};

const MultiValue = (props: MultiValueProps<any>) => {
  return (
    <components.MultiValue {...props}>{props.data.label}</components.MultiValue>
  );
};

const selectAllOption = {
  value: 'all',
  label: 'Select all'
};

const DynamicSelect: React.FC<Props> = ({
  defaultValue,
  isClearable,
  isMulti,
  options,
  name,
  onChange,
  inputId,
  styles,
  isLoading,
  fetchOptions,
  dataTestId,
  placeholder,
  label,
  dataTrackSelect
}) => {
  const valueRef = useRef(defaultValue);
  valueRef.current = defaultValue;

  function getOptions() {
    if (isLoading || options.length === 0) return [];
    return [selectAllOption, ...options];
  }

  function isAllSelected(): boolean {
    if (!isNil(valueRef.current) && valueRef.current instanceof Array) {
      return getOptions().length === valueRef.current.length;
    }

    return false;
  }

  function isOptionSelected(option: any) {
    if (
      !isNil(valueRef.current) &&
      valueRef.current instanceof Array &&
      valueRef.current.length > 0
    ) {
      return (
        valueRef.current.some((value: string) => value === option.value) ||
        isAllSelected()
      );
    } else {
      return false;
    }
  }

  // We use any here since the value of react select expects
  // a type that conflicts what we need.
  // TODO: recheck this
  function processValue(): any {
    let value: SelectOption | SelectOption[] | null = [];
    let selected: SelectOption | undefined;

    if (isNil(valueRef.current) || valueRef.current.length === 0) {
      return value;
    }

    const allOptions = getOptions();

    if (!isNil(valueRef.current)) {
      // Handles single value
      if (!(valueRef.current instanceof Array)) {
        selected = allOptions.find(
          (option: SelectOption) =>
            valueRef.current &&
            option.value.toString() === valueRef.current.toString()
        );
        if (!isNil(selected)) value = selected;
      } else {
        // Handles multiple values
        if (isAllSelected()) {
          value = [selectAllOption];
        } else {
          const arr: SelectOption[] = [];
          valueRef.current.forEach((o: string) => {
            selected = allOptions.find(
              (option: SelectOption) =>
                !isNil(o) && option.value.toString() === o.toString()
            );
            if (!isNil(selected)) arr.push(selected);
          });
          value = arr;
          return value;
        }
      }
    }

    return value;
  }

  function handleChange(
    selectValue: SelectOption[] | SelectOption,
    meta: any
  ): void {
    let value: string | string[] | null = isMulti ? [] : null;

    if (isNil(selectValue)) {
      onChange(value);
    }

    if (selectValue instanceof Array) {
      const { action, option } = meta;
      const allOptions = getOptions();

      const selectedAll = (): boolean => {
        return (
          action === 'select-option' && option.value === selectAllOption.value
        );
      };

      const deSelectedAll = (): boolean => {
        return (
          action === 'deselect-option' && option.value === selectAllOption.value
        );
      };

      const allSelectedAndDeselectOther = (): boolean => {
        return (
          action === 'deselect-option' &&
          !isNil(valueRef.current) &&
          valueRef.current.length === allOptions.length
        );
      };

      if (selectedAll()) value = allOptions.map((o: SelectOption) => o.value);
      else if (deSelectedAll()) value = [];
      else if (allSelectedAndDeselectOther()) {
        value = options
          .filter(({ value }) => value !== option.value)
          .map((option: SelectOption) => option.value);
      } else {
        value =
          selectValue.length !== 0
            ? selectValue.map((option: SelectOption) => option.value)
            : [];
      }
    } else if (selectValue instanceof Object) {
      value = selectValue.value;
    }
    if (dataTrackSelect) trackChange(dataTrackSelect, value as string);
    onChange(value);
  }

  function trackChange(prefix: string, value: string) {
    let track_value = value;
    const customValues = Object.values(CUSTOM_VALUES);
    let values = [prefix];
    if (customValues.includes(value as CUSTOM_VALUES)) {
      const index = customValues.indexOf(value as CUSTOM_VALUES);
      track_value = Object.keys(CUSTOM_VALUES)[index];
      values = [track_value];
    } else {
      values.push(track_value as string);
    }
    values.push('selected');
    trackEvent(values.join('_'));
  }

  function onMenuOpen(): void {
    if (!isNil(fetchOptions) && options.length === 0) fetchOptions();
  }

  if (isMulti) {
    return (
      <div data-testid={dataTestId}>
        <Select
          placeholder={placeholder}
          value={processValue()}
          name={name}
          inputId={inputId}
          closeMenuOnSelect={false}
          isMulti={true}
          components={{
            Option,
            MultiValue
          }}
          options={getOptions()}
          onChange={handleChange}
          hideSelectedOptions={false}
          styles={{ ...multiStyles, ...styles }}
          isLoading={isLoading}
          onMenuOpen={onMenuOpen}
          isOptionSelected={isOptionSelected}
          onBlur={(event: any) => event.preventDefault()}
          dataTrackSelect={dataTrackSelect}
        />
      </div>
    );
  } else {
    return (
      <div data-testid={dataTestId}>
        <label
          className="ds-input position-relative _marginBottom--xxxsmall"
          data-testid={`${dataTestId}-input`}
          htmlFor={inputId}
        >
          <span>{label}</span>
        </label>
        <Select
          placeholder={placeholder}
          value={processValue()}
          options={options}
          name={name}
          inputId={inputId}
          styles={{ ...singleStyles, ...styles }}
          isClearable={isClearable}
          onChange={handleChange}
          isLoading={isLoading}
          onMenuOpen={onMenuOpen}
          menuPlacement="auto"
          onMenu
          dataTrackSelect={dataTrackSelect}
        />
      </div>
    );
  }
};

export default DynamicSelect;
